import { queryClient } from "@/query-client";
import { queryOptions, useMutation } from "@tanstack/react-query";
import type { CreateInvoiceRequestData, UpdateInvoiceRequestData } from "../../types/invoice-request";
import invoiceRequestService from "../services/invoice-request-service";

export const invoiceRequestPendingCountQueryOptions = () =>
	queryOptions({
		queryKey: ["invoice-requests", "pending-count"],
		queryFn: () => invoiceRequestService.pendingCount(),
		refetchInterval: 60000,
	});

export const invoiceRequestsQueryOptions = (params: Record<string, unknown> = {}) =>
	queryOptions({
		queryKey: ["invoice-requests", params],
		queryFn: () => invoiceRequestService.list(params),
	});

export const invoiceRequestQueryOptions = (id: string) =>
	queryOptions({
		queryKey: ["invoice-request", id],
		queryFn: () => invoiceRequestService.findById(id),
	});

export const invoiceRequestJournalItemsQueryOptions = (id: string) =>
	queryOptions({
		queryKey: ["invoice-request", id, "journal-items"],
		queryFn: () => invoiceRequestService.getJournalItems(id),
	});

export const usePushInvoiceRequestDraftsMutation = (id: string) =>
	useMutation({
		mutationKey: ["invoice-request", id, "push-drafts"],
		mutationFn: (status: "draft" | "posted" = "draft") => invoiceRequestService.pushDrafts(id, status),
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: ["invoice-request", id] });
			queryClient.invalidateQueries({ queryKey: ["invoice"] });
		},
	});

export const useInvoiceRequestCreateMutation = () =>
	useMutation({
		mutationFn: (data: CreateInvoiceRequestData) => invoiceRequestService.create(data),
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: ["invoice-requests"] });
			// Detail-page queries live under the singular key (e.g.,
			// ["invoice-request", id], ["invoice-request", id, "journal-items"],
			// ["invoice-request", id, "collection-items"]). Invalidating the
			// prefix refreshes the page header, both JE tabs, and the
			// payment breakdown all at once after a mutation.
			queryClient.invalidateQueries({ queryKey: ["invoice-request"] });
		},
	});

export const useInvoiceRequestUpdateMutation = (id: string) =>
	useMutation({
		mutationFn: (data: UpdateInvoiceRequestData) => invoiceRequestService.updateById(id, data),
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: ["invoice-requests"] });
			queryClient.invalidateQueries({ queryKey: ["invoice-request", id] });
		},
	});

export const useInvoiceRequestDeleteMutation = () =>
	useMutation({
		mutationFn: (id: string) => invoiceRequestService.deleteById(id),
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: ["invoice-requests"] });
			// Detail-page queries live under the singular key (e.g.,
			// ["invoice-request", id], ["invoice-request", id, "journal-items"],
			// ["invoice-request", id, "collection-items"]). Invalidating the
			// prefix refreshes the page header, both JE tabs, and the
			// payment breakdown all at once after a mutation.
			queryClient.invalidateQueries({ queryKey: ["invoice-request"] });
		},
	});

export const useInvoiceRequestStatusMutation = () =>
	useMutation({
		mutationFn: ({ id, ...data }: { id: string; status: string; payment_due_date?: string }) => invoiceRequestService.updateStatus(id, data),
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: ["invoice-requests"] });
			// Detail-page queries live under the singular key (e.g.,
			// ["invoice-request", id], ["invoice-request", id, "journal-items"],
			// ["invoice-request", id, "collection-items"]). Invalidating the
			// prefix refreshes the page header, both JE tabs, and the
			// payment breakdown all at once after a mutation.
			queryClient.invalidateQueries({ queryKey: ["invoice-request"] });
		},
	});

export const useInvoiceRequestMarkSentMutation = () =>
	useMutation({
		mutationFn: ({
			id,
			...data
		}: {
			id: string;
			sent_date?: string;
			delivery_method: string;
			recipient_notes?: string;
		}) => invoiceRequestService.markSent(id, data),
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: ["invoice-requests"] });
			queryClient.invalidateQueries({ queryKey: ["invoice-request"] });
		},
	});

export const useInvoiceRequestPaymentStatusMutation = () =>
	useMutation({
		mutationFn: ({
			id,
			...data
		}: {
			id: string;
			payment_status: string;
			follow_up_notes?: string;
			payment_amount?: number;
			payment_date?: string;
			payment_reference_number?: string;
			payment_method?: string;
			payment_attachment?: File;
		}) => invoiceRequestService.updatePaymentStatus(id, data),
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: ["invoice-requests"] });
			// Detail-page queries live under the singular key (e.g.,
			// ["invoice-request", id], ["invoice-request", id, "journal-items"],
			// ["invoice-request", id, "collection-items"]). Invalidating the
			// prefix refreshes the page header, both JE tabs, and the
			// payment breakdown all at once after a mutation.
			queryClient.invalidateQueries({ queryKey: ["invoice-request"] });
		},
	});

export const useInvoiceRequestCorrectionsMutation = () =>
	useMutation({
		mutationFn: ({
			id,
			items,
		}: {
			id: string;
			items: Array<{
				quantity: number;
				unit_of_measurement: string;
				item_description: string;
				unit_price: number;
				total_price: number;
			}>;
		}) => invoiceRequestService.updateCorrections(id, { items }),
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: ["invoice-requests"] });
			// Detail-page queries live under the singular key (e.g.,
			// ["invoice-request", id], ["invoice-request", id, "journal-items"],
			// ["invoice-request", id, "collection-items"]). Invalidating the
			// prefix refreshes the page header, both JE tabs, and the
			// payment breakdown all at once after a mutation.
			queryClient.invalidateQueries({ queryKey: ["invoice-request"] });
		},
	});

export const useInvoiceRequestPaymentBreakdownMutation = () =>
	useMutation({
		mutationFn: ({
			id,
			...data
		}: {
			id: string;
			deduction_withholding_tax?: number | null;
			deduction_liquidated_damages?: number | null;
			deduction_retention?: number | null;
			deduction_municipal_charge?: number | null;
			deduction_bank_charge?: number | null;
		}) => invoiceRequestService.updatePaymentBreakdown(id, data),
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: ["invoice-requests"] });
			// Detail-page queries live under the singular key (e.g.,
			// ["invoice-request", id], ["invoice-request", id, "journal-items"],
			// ["invoice-request", id, "collection-items"]). Invalidating the
			// prefix refreshes the page header, both JE tabs, and the
			// payment breakdown all at once after a mutation.
			queryClient.invalidateQueries({ queryKey: ["invoice-request"] });
		},
	});

export const useInvoiceRequestNotifyClientMutation = () =>
	useMutation({
		mutationFn: ({ id, ...data }: { id: string; email: string; subject: string; message: string }) => invoiceRequestService.notifyClient(id, data),
	});

export const useInvoiceRequestUploadAttachmentMutation = () =>
	useMutation({
		mutationFn: ({ id, file }: { id: string; file: File }) => invoiceRequestService.uploadAttachment(id, file),
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: ["invoice-requests"] });
			// Detail-page queries live under the singular key (e.g.,
			// ["invoice-request", id], ["invoice-request", id, "journal-items"],
			// ["invoice-request", id, "collection-items"]). Invalidating the
			// prefix refreshes the page header, both JE tabs, and the
			// payment breakdown all at once after a mutation.
			queryClient.invalidateQueries({ queryKey: ["invoice-request"] });
		},
	});

export const useUpdateJournalLinesMutation = (requestId: string) =>
	useMutation({
		mutationKey: ["invoice-request", requestId, "journal-lines", "update"],
		mutationFn: (lines: import("../../types/invoice").InvoiceJournalLine[]) =>
			invoiceRequestService.updateJournalLines(requestId, { lines }),
		onSuccess: () => {
			queryClient.refetchQueries({ queryKey: ["invoice-request", requestId, "journal-items"] });
			queryClient.invalidateQueries({ queryKey: ["invoice-request", requestId] });
		},
	});

export const useResetJournalLinesMutation = (requestId: string) =>
	useMutation({
		mutationKey: ["invoice-request", requestId, "journal-lines", "reset"],
		mutationFn: () => invoiceRequestService.resetJournalLines(requestId),
		onSuccess: () => {
			queryClient.refetchQueries({ queryKey: ["invoice-request", requestId, "journal-items"] });
			queryClient.invalidateQueries({ queryKey: ["invoice-request", requestId] });
		},
	});
